import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEnvelope, ApiResult, classifyHttpError, unwrapEnvelope } from './api-result';
import {
  CreateAddressRequest,
  CustomerAddress,
  CustomerMeResponse,
  CustomerOrderDetail,
  CustomerOrderSummary,
  DeliveryOrderResponse,
  DeliveryPaymentIntentRequest,
  DeliveryQuoteResponse,
  OtpRequestResult,
  OtpVerifyResponse,
  PaymentIntentResponse,
  PaymentIntentStatus,
  PlaceDeliveryOrderRequest,
  UpdateCustomerProfileRequest, CancellationRequestResponse } from './customer-dtos';

/**
 * D4A — typed wrapper over the D2 customer + D3A delivery endpoints. Every
 * method returns a discriminated `ApiResult<T>` (never a raw HttpErrorResponse),
 * so components map cleanly to user-facing states. The X-Customer-Token header
 * is added transparently by the interceptor for eligible URLs.
 */
@Injectable({ providedIn: 'root' })
export class CustomerApiService {
  private http = inject(HttpClient);
  private base = environment.apiUrl;

  // ── OTP (open, store-token scoped) ──
  // D7 — OTP by EITHER mobile (WhatsApp) OR email. Pass whichever the guest used.
  requestOtp(storeToken: string, mobile: string | null, email?: string | null): Observable<ApiResult<OtpRequestResult>> {
    return this.post<OtpRequestResult>(`${this.base}/public/customer/otp/request`,
      { storeToken, mobile: mobile ?? null, email: email ?? null, purpose: 'Login' });
  }

  verifyOtp(storeToken: string, mobile: string | null, email: string | null, code: string): Observable<ApiResult<OtpVerifyResponse>> {
    return this.post<OtpVerifyResponse>(`${this.base}/public/customer/otp/verify`,
      { storeToken, mobile: mobile ?? null, email: email ?? null, code });
  }

  // ── Profile (requires X-Customer-Token) ──
  getMe(): Observable<ApiResult<CustomerMeResponse>> {
    return this.get<CustomerMeResponse>(`${this.base}/public/customer/me`);
  }
  updateMe(body: UpdateCustomerProfileRequest): Observable<ApiResult<CustomerMeResponse>> {
    return this.patch<CustomerMeResponse>(`${this.base}/public/customer/me`, body);
  }

  // ── Addresses (requires X-Customer-Token; own-data only) ──
  listAddresses(): Observable<ApiResult<CustomerAddress[]>> {
    return this.get<CustomerAddress[]>(`${this.base}/public/customer/addresses`);
  }
  addAddress(body: CreateAddressRequest): Observable<ApiResult<CustomerAddress>> {
    return this.post<CustomerAddress>(`${this.base}/public/customer/addresses`, body);
  }
  updateAddress(addressId: string, body: Partial<CreateAddressRequest>): Observable<ApiResult<CustomerAddress>> {
    return this.patch<CustomerAddress>(`${this.base}/public/customer/addresses/${encodeURIComponent(addressId)}`, body);
  }
  deleteAddress(addressId: string): Observable<ApiResult<boolean>> {
    return this.delete<boolean>(`${this.base}/public/customer/addresses/${encodeURIComponent(addressId)}`);
  }

  // ── Orders (D10 — requires X-Customer-Token; own-data only, read-only) ──
  listOrders(): Observable<ApiResult<CustomerOrderSummary[]>> {
    return this.get<CustomerOrderSummary[]>(`${this.base}/public/customer/orders`);
  }
  getOrder(orderReference: string): Observable<ApiResult<CustomerOrderDetail>> {
    return this.get<CustomerOrderDetail>(`${this.base}/public/customer/orders/${encodeURIComponent(orderReference)}`);
  }
  /** CANX-4 — request cancellation of an own, not-yet-accepted delivery order.
   *  Server-guarded (window, one-per-order, store flag); 409s carry an honest
   *  message + code (order_already_accepted / order_closed / ...). */
  requestCancellation(orderReference: string, reason?: string | null): Observable<ApiResult<CancellationRequestResponse>> {
    return this.post<CancellationRequestResponse>(
      `${this.base}/public/customer/orders/${encodeURIComponent(orderReference)}/cancellation-request`,
      { reason: reason ?? null });
  }

  // ── Delivery order (open; optional X-Customer-Token) ──
  submitDeliveryOrder(storeToken: string, body: PlaceDeliveryOrderRequest): Observable<ApiResult<DeliveryOrderResponse>> {
    return this.post<DeliveryOrderResponse>(
      `${this.base}/public/store/${encodeURIComponent(storeToken)}/delivery/orders`, body);
  }

  /** Delivery pricing preview — Delivery-channel breakdown + total for a cart
   *  subtotal, so checkout shows what will actually be charged. */
  quoteDelivery(storeToken: string, subtotal: number): Observable<ApiResult<DeliveryQuoteResponse>> {
    return this.post<DeliveryQuoteResponse>(
      `${this.base}/public/store/${encodeURIComponent(storeToken)}/delivery/quote`, { subtotal });
  }

  // ── D3B — delivery online (card) payment ──
  /** Create a pay-first card payment intent for a delivery order (Stripe/Magnati). */
  createDeliveryPaymentIntent(
    storeToken: string, body: DeliveryPaymentIntentRequest): Observable<ApiResult<PaymentIntentResponse>> {
    return this.post<PaymentIntentResponse>(
      `${this.base}/public/store/${encodeURIComponent(storeToken)}/delivery/payment-intents`, body);
  }

  /** Poll payment-intent status (shared public endpoint; works for delivery + pickup intents). */
  getPaymentIntentStatus(storeToken: string, paymentIntentId: string): Observable<ApiResult<PaymentIntentStatus>> {
    return this.get<PaymentIntentStatus>(
      `${this.base}/public/table-access/${encodeURIComponent(storeToken)}/payment-intents/${encodeURIComponent(paymentIntentId)}/status`);
  }

  // ── transport helpers ──
  private get<T>(url: string): Observable<ApiResult<T>> {
    return this.http.get<ApiEnvelope<T>>(url).pipe(
      map(unwrapEnvelope),
      catchError((e: HttpErrorResponse) => of(classifyHttpError<T>(e))));
  }
  private post<T>(url: string, body: unknown): Observable<ApiResult<T>> {
    return this.http.post<ApiEnvelope<T>>(url, body).pipe(
      map(unwrapEnvelope),
      catchError((e: HttpErrorResponse) => of(classifyHttpError<T>(e))));
  }
  private patch<T>(url: string, body: unknown): Observable<ApiResult<T>> {
    return this.http.patch<ApiEnvelope<T>>(url, body).pipe(
      map(unwrapEnvelope),
      catchError((e: HttpErrorResponse) => of(classifyHttpError<T>(e))));
  }
  private delete<T>(url: string): Observable<ApiResult<T>> {
    return this.http.delete<ApiEnvelope<T>>(url).pipe(
      map(unwrapEnvelope),
      catchError((e: HttpErrorResponse) => of(classifyHttpError<T>(e))));
  }
}
